/**
 * Every Timesheet Approvals API call, in one place.
 *
 * All of these go through `api2.js`, which handles the processing overlay, the failure toast and
 * the logging centrally. Callers assume the happy path and only need the single
 * `if (responseData == null) return;` check.
 *
 * The routes and the request/response shapes are the back-end's
 * `Lotus.Module.Timesheets/API/Approvals/*` handlers and `Models/ApprovalsModels.cs`
 * (controller: `Lotus.MvcAPI/Controllers/Timesheets/TimesheetApprovalsController.cs`). Properties
 * are PascalCase on the wire.
 *
 * Reads are GETs (`?Mode=Week&WeekStartDate=2026-07-06`); actions are POSTs with a JSON body.
 * A refusal (not an approver, a Timesheet outside your scope) is an HTTP 403 with
 * `Lotus-Status-Code: not-authorised`, which `api2.js` handles like any other failure.
 *
 * Dates are "yyyy-MM-dd" (a day) or ISO-8601 UTC (an instant). `DateContentChangedUTC` is a
 * content version: send back exactly the value the load returned, never a reformatted one.
 *
 * `callApi1` (no overlay) is used for the two reads that run in the background for the
 * navigation badges, so the processing overlay doesn't flash on every page change.
 */

import api2 from "/api2.js";


const BASE = "/Timesheets/Approvals";




//-- Availability and the navigation badges (no overlay)

/**
 * Whether the Approvals workspace exists for the signed-in user: is approval switched on for the
 * account, are they an approver, and how wide is their reach.
 *
 * @returns {Promise<{ ApprovalsEnabled: boolean, IsApprover: boolean, ScopeKind: string, HasMappedTeam: boolean }|null>}
 */
export async function getApprovalsAvailabilityAsync()
{
	return await api2.callApi1("GET", BASE + "/GetApprovalsAvailability/v1", {});
}


/**
 * The number of days waiting for the signed-in approver (the Approvals nav badge). Refused for
 * anyone who isn't an approver, so call it only when availability says they are.
 *
 * @returns {Promise<{ PendingCount: number }|null>}
 */
export async function getApprovalCountsAsync()
{
	return await api2.callApi1("GET", BASE + "/GetApprovalCounts/v1", {});
}




/**
 * The signed-in user's own count of Timesheets still needing action (Draft or Rejected) in the
 * Approvals window (the current week and the weeks before it, up to yesterday: today isn't counted), plus the oldest such
 * day: the Home badge under Timesheets (decided 2026-10-03: the badge is what is actionable now,
 * not the whole history). The server counts it in the database on every call, so it is never a
 * stored copy. This is the Timesheets controller's endpoint, not an Approvals one; it sits here
 * because the nav badges are its only caller outside edit-timesheet2, which asks for the full
 * history.
 *
 * @returns {Promise<{ Count: number, OldestOutstandingDate: string, WindowStartDate: string }|null>}
 */
export async function getMyOutstandingTimesheetCountAsync()
{
	return await api2.callApi1("GET", "/Timesheets/GetUserOutstandingTimesheetCount", { WithinApprovalsWindow: true });
}




//-- The workspace reads

/**
 * The Overview matrix: one row per timekeeper in the approver's scope, one cell per date.
 *
 * @param {string} mode "Week" (default) or "Earlier".
 * @param {string} weekStartDate Any date in the week (yyyy-MM-dd); blank means the current week.
 * @returns {Promise<object|null>} `{ Mode, WeekStartDate, Dates, Timekeepers, Counts, Backlog, ScopeKind, HasMappedTeam, WindowStartDate, WindowEndDate }`
 */
export async function getApprovalsOverviewAsync(mode, weekStartDate)
{
	const request = { Mode: mode || "Week", WeekStartDate: weekStartDate || "" };

	return await api2.callApi2WithOverlay("GET", BASE + "/GetApprovalsOverview/v1", request);
}


/**
 * The pending days for the Bulk approve screen, for the same context as the Overview.
 *
 * @returns {Promise<{ Days: object[] }|null>}
 */
export async function getPendingApprovalsAsync(mode, weekStartDate)
{
	const request = { Mode: mode || "Week", WeekStartDate: weekStartDate || "" };

	return await api2.callApi2WithOverlay("GET", BASE + "/GetPendingApprovals/v1", request);
}


/**
 * One day for review: its entries, variance against what capture produced, review history and the
 * activity held back since submission.
 *
 * @returns {Promise<object|null>} The `ApprovalsDayData` shape, flat on the response.
 */
export async function getApprovalDayAsync(timesheetId)
{
	const request = { TimesheetID: timesheetId };

	return await api2.callApi2WithOverlay("GET", BASE + "/GetApprovalDay/v1", request);
}


/**
 * The people with time not yet submitted, with the days missing and the reminders already sent.
 *
 * @returns {Promise<{ People: object[], PeopleCount: number }|null>}
 */
export async function getOutstandingSubmissionsAsync()
{
	return await api2.callApi2WithOverlay("GET", BASE + "/GetOutstandingSubmissions/v1", {});
}


/**
 * The active reasons the Reject modal offers, in order.
 *
 * @returns {Promise<{ Reasons: { ID: string, Label: string, RequiresComment: boolean, SortOrder: number }[] }|null>}
 */
export async function getRejectionReasonsAsync()
{
	return await api2.callApi2WithOverlay("GET", BASE + "/GetRejectionReasons/v1", {});
}




//-- The actions

/**
 * Approve one day, or several (Bulk). One outcome per item; one bad item never fails the rest.
 *
 * @param {{ TimesheetID: string, DateContentChangedUTC: string }[]} items Each with the content version the screen loaded.
 * @param {string} manner "Individual" or "Bulk".
 * @returns {Promise<{ Results: { TimesheetID: string, Outcome: string, NewStatus: string }[], ApprovedCount: number, CorrelationID: string }|null>}
 */
export async function approveTimesheetsAsync(items, manner)
{
	const request = { Items: items, Manner: manner || "Individual" };

	return await api2.callApi2WithOverlay("POST", BASE + "/ApproveTimesheets/v1", request);
}


/**
 * Send one Submitted day back to its owner with a reason. Emails the owner.
 *
 * @returns {Promise<{ Outcome: string, NewStatus: string }|null>}
 */
export async function rejectTimesheetAsync(timesheetId, dateContentChangedUtc, rejectionReasonId, comment)
{
	const request =
	{
		TimesheetID: timesheetId,
		DateContentChangedUTC: dateContentChangedUtc,
		RejectionReasonID: rejectionReasonId,
		Comment: comment || "",
	};

	return await api2.callApi2WithOverlay("POST", BASE + "/RejectTimesheet/v1", request);
}


/**
 * The manual nudge: email each person the days they have not submitted. Sends real email, so the
 * caller confirms first. One outcome per person.
 *
 * @param {string[]} userIds
 * @returns {Promise<{ Results: { UserID: string, Outcome: string }[], SentCount: number }|null>}
 */
export async function sendTimesheetRemindersAsync(userIds)
{
	const request = { UserIDs: userIds };

	return await api2.callApi2WithOverlay("POST", BASE + "/SendTimesheetReminders/v1", request);
}
